// The project's class-level view — ported from the prototype demo's project
// page (app/demo/[projectId]/page.jsx on branch mosaico-demo) minus its page
// header and presenter controls, which the Mosaico page frame now provides.
//
// Composed over the same primitives as the prototype's ClassOverview: Disc +
// ClassDiscPopover for the disc, and four of its data views imported
// verbatim (StandardsCoverage, CompetencyPerformance, SkillsNeedingAttention,
// SkillHeatmap) with their drill-downs (GradeDetailModal, SubmissionViewer).
//
// Before the assessment runs the disc is drawn BLANK (every wedge white, the
// full curriculum shape) and the score-driven views stay hidden; as students
// are revealed the disc blooms into colour (see useBloom + Disc's `reveal`)
// and the views appear.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Disc } from '../components/disc';
import ClassDiscPopover from '../components/overview/ClassDiscPopover';
import CompetencyPerformance from '../components/overview/CompetencyPerformance';
import SkillsNeedingAttention from '../components/overview/SkillsNeedingAttention';
import SkillHeatmap from '../components/overview/SkillHeatmap';
import ClickedSkillPanel from '../components/overview/ClickedSkillPanel';
import SubmissionViewer from '../components/overview/SubmissionViewer';
import StandardsCoverage, { buildStandardsCoverage } from '../components/overview/StandardsCoverage';
import GradeDetailModal from '../components/features/GradeDetailModal';
import RubricLadder from '../components/features/RubricLadder';
import { useProjectDashboard, useProjectGrades, useProjectReview } from '../data/hooks';
import { demoStudentPath } from '../data/projects';
import { useBloom } from './useBloom';
import { AssessmentProgress } from './AssessmentProgress';
import { useProjectDemoState } from '../data/demoState';

function Card({ className = '', children }) {
  return (
    <div className={`rounded-2xl border border-border bg-card shadow-card ${className}`}>{children}</div>
  );
}

function initialsOf(name) {
  return (name || '?')
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export default function ClassOverviewSection({ adProjectId, cardId }) {
  const { data, isLoading, error } = useProjectDashboard(adProjectId);
  const reviewQuery = useProjectReview(adProjectId);

  // Collapsed by default so the disc stays in the first screen; one click opens it.
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [filter, setFilter] = useState(null);
  const [clickedSkillNode, setClickedSkillNode] = useState(null);
  const [cellDetail, setCellDetail] = useState(null);
  const [viewerStudent, setViewerStudent] = useState(null);
  const [viewerAnchor, setViewerAnchor] = useState(null);
  const matrixRef = useRef(null);

  const gradesQuery = useProjectGrades(adProjectId);

  const standardsCoverage = useMemo(
    () =>
      buildStandardsCoverage(
        reviewQuery.data?.standards ?? [],
        reviewQuery.data?.mappings ?? [],
        data?.skills ?? []
      ),
    [reviewQuery.data, data]
  );

  const filteredSpecificSkillIds = useMemo(() => {
    if (!filter || !data) return null;
    if (filter.kind === 'specific_skill_id') return new Set([filter.id]);
    if (filter.kind === 'competency_id') {
      return new Set(
        data.skills.filter((s) => s.competency_id === filter.id).map((s) => s.specific_skill_id)
      );
    }
    if (filter.kind === 'standard') return new Set(filter.skillIds);
    return null;
  }, [filter, data]);

  const cellGrades = useMemo(() => {
    if (!cellDetail || !gradesQuery.data) return [];
    return gradesQuery.data
      .filter(
        (g) =>
          g.student_id === cellDetail.student.student_id &&
          g.specific_skill_id === cellDetail.skillRow.specific_skill_id
      )
      .sort((a, b) => (b.attempt_no ?? 0) - (a.attempt_no ?? 0));
  }, [cellDetail, gradesQuery.data]);

  const graded = data ? data.students.filter((s) => s.skills_graded > 0).length : 0;
  const bloom = useBloom(graded > 0);

  // The reveal is the demo's key moment, so it must happen on screen: the
  // progress panel + disc card are the first thing under the tabs, and when a
  // run starts they are scrolled into view (the Mosaico top bar is fixed, so
  // the wrapper carries a scroll margin).
  const { status } = useProjectDemoState(adProjectId);
  const stageRef = useRef(null);
  useEffect(() => {
    if (status === 'grading') {
      requestAnimationFrame(() =>
        stageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      );
    }
  }, [status]);

  function handleDiscClick(node) {
    if (!node) {
      if (clickedSkillNode) setClickedSkillNode(null);
      return;
    }
    if (clickedSkillNode?.id === node.id) {
      setClickedSkillNode(null);
      return;
    }
    setClickedSkillNode(node);
  }

  function selectStandard(row) {
    setFilter(
      row ? { kind: 'standard', id: row.standard_path, label: row.code, skillIds: row.skillIds } : null
    );
    if (row) {
      requestAnimationFrame(() =>
        matrixRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      );
    }
  }

  if (isLoading) return <p className="text-[15px] text-muted-foreground">Loading project…</p>;
  if (error) return <p className="text-[15px] text-muted-foreground">{error.message}</p>;
  if (!data) return null;

  const { students, curriculum, scores, skills, matrix } = data;

  // Strongest / weakest competency from the same class_avg field
  // CompetencyPerformance renders. Deterministic tiebreak: avg desc, then
  // more graded evidence, then name.
  const competencyStats = (() => {
    const byId = new Map();
    for (const s of skills) {
      if (s.class_avg == null) continue;
      const row = byId.get(s.competency_id) ?? { competency: s.competency, sum: 0, n: 0 };
      row.sum += s.class_avg;
      row.n += 1;
      byId.set(s.competency_id, row);
    }
    return Array.from(byId.values())
      .map((r) => ({ competency: r.competency, avg: r.sum / r.n, n: r.n }))
      .sort((a, b) => b.avg - a.avg || b.n - a.n || a.competency.localeCompare(b.competency));
  })();
  const strongestCompetency = competencyStats[0] ?? null;
  const weakestCompetency =
    competencyStats.length > 1 ? competencyStats[competencyStats.length - 1] : null;

  return (
    <div className="flex flex-col gap-6">
      {/* Stage: progress narration + the disc, first under the tabs. */}
      <div ref={stageRef} className="scroll-mt-20 flex flex-col gap-6">
        <AssessmentProgress adProjectId={adProjectId} />
      {/* The class disc — ALWAYS drawn. Before assessment every wedge is white
          (the full curriculum shape, nothing developed); after, it blooms. */}
      <Card className="px-6 py-6">
        <h2 className="text-[16px] font-semibold text-foreground">Class skill map</h2>
        <p className="mt-1.5 text-[14px] text-muted-foreground">
          Three rings of curriculum, innermost outwards: area, competency, skill.{' '}
          {graded > 0 ? (
            <>
              Hover any segment for its mastery and class average.{' '}
              <strong className="font-semibold text-foreground">Click an area or competency</strong> to
              zoom into it;{' '}
              <strong className="font-semibold text-foreground">click a skill cell</strong> to see the
              skill developmental descriptors it covers. Disc clicks do not affect the matrix below.
            </>
          ) : (
            <>
              Every segment is white because nothing has been assessed yet — the disc fills with
              colour as the AI assessment scores each student’s work.
            </>
          )}
        </p>
        {(strongestCompetency || weakestCompetency) && (
          <div className="mt-4 flex flex-wrap gap-3">
            {strongestCompetency && (
              <div className="min-w-[220px] flex-1 rounded-xl border border-border bg-secondary px-4 py-2.5">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Strongest competency
                </div>
                <div className="mt-0.5 text-[14px] font-semibold text-foreground">
                  {strongestCompetency.competency}
                </div>
                <div className="text-[12px] text-muted-foreground">
                  {strongestCompetency.avg.toFixed(1)} / 5 class average
                </div>
              </div>
            )}
            {weakestCompetency && (
              <div className="min-w-[220px] flex-1 rounded-xl border border-border bg-secondary px-4 py-2.5">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Needs the most support
                </div>
                <div className="mt-0.5 text-[14px] font-semibold text-foreground">
                  {weakestCompetency.competency}
                </div>
                <div className="text-[12px] text-muted-foreground">
                  {weakestCompetency.avg.toFixed(1)} / 5 class average
                </div>
              </div>
            )}
          </div>
        )}
        <div className="mt-5 grid gap-6 min-[1100px]:grid-cols-[minmax(0,1fr)_320px]">
          <div className="flex justify-center">
            {/* Class tree nodes carry no top-level `kind`, so no kind prop:
                passing kind="content" would filter every area out. */}
            <Disc
              curriculum={curriculum}
              scores={scores}
              width={560}
              height={560}
              reveal={bloom}
              onClick={handleDiscClick}
              popoverComponent={(p) => <ClassDiscPopover {...p} compactInnerRings />}
            />
          </div>
          <ClickedSkillPanel
            clickedNode={clickedSkillNode}
            allSkills={skills}
            onClear={() => setClickedSkillNode(null)}
          />
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-5 text-[12px] text-muted-foreground">
          <span>Each area has its own colour; its competencies and skills inherit it.</span>
          <span className="flex items-center gap-2.5">
            <span>Color depth = students at level 4+</span>
            <span className="flex flex-col gap-0.5">
              <span className="h-2.5 w-28 rounded-full bg-[linear-gradient(to_right,var(--secondary),var(--primary))]" />
              <span className="flex w-28 justify-between text-[10px] leading-none">
                <span>none</span>
                <span>all</span>
              </span>
            </span>
          </span>
        </div>
      </Card>
      </div>

      {/* Orientation: what the disc means and the rubric every descriptor is
          graded against. */}
      <Card className="px-6 py-5">
        <button
          type="button"
          onClick={() => setShowHowItWorks((v) => !v)}
          className="flex w-full items-center justify-between gap-3 text-left"
        >
          <h2 className="text-[16px] font-semibold text-foreground">How to read this</h2>
          <span className="text-[12.5px] font-medium text-muted-foreground">
            {showHowItWorks ? 'Hide ▾' : 'Show ▸'}
          </span>
        </button>
        {showHowItWorks && (
          <div className="mt-4 grid gap-6 md:grid-cols-[minmax(0,1fr)_320px]">
            <div className="flex flex-col gap-5">
              <div className="flex items-stretch gap-1.5">
                {[
                  { n: 1, label: 'Skill', desc: 'Area → Competency → Skill — the disc’s 3 rings' },
                  {
                    n: 2,
                    label: 'Skill developmental descriptor',
                    desc: 'The finest-grained behavior — what grading actually scores — mapped to a real curriculum standard',
                  },
                  { n: 3, label: 'Graded 1–5', desc: 'AI reads the work, scores it like a teacher would' },
                ].map((step, i, arr) => (
                  <React.Fragment key={step.n}>
                    <div className="flex-1 rounded-xl border border-border bg-secondary px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                          {step.n}
                        </span>
                        <span className="text-[12.5px] font-semibold text-foreground">{step.label}</span>
                      </div>
                      <div className="mt-1 text-[11.5px] leading-snug text-muted-foreground">{step.desc}</div>
                    </div>
                    {i < arr.length - 1 && (
                      <span aria-hidden className="flex shrink-0 items-center text-[16px] text-muted-foreground">
                        →
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="flex flex-col gap-1.5">
                {[
                  { size: 10, label: 'Area', desc: 'a domain of the framework' },
                  { size: 16, label: 'Competency', desc: 'a capability students develop' },
                  {
                    size: 22,
                    label: 'Skill',
                    desc: 'an observable ability within a competency — click an arc to see its descriptors',
                  },
                ].map((ring) => (
                  <div key={ring.label} className="flex items-center gap-2.5 text-[12.5px]">
                    <span
                      aria-hidden
                      className="shrink-0 rounded-full border-2 border-primary"
                      style={{ width: ring.size, height: ring.size }}
                    />
                    <span className="font-semibold text-foreground">{ring.label}</span>
                    <span className="text-muted-foreground">{ring.desc}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-1.5 text-[12px] text-muted-foreground">
                Every descriptor is graded on this same 1–5 rubric:
              </div>
              <RubricLadder />
            </div>
          </div>
        )}
      </Card>

      {/* Roster — the way into a student's own disc, true from the moment the
          project is ingested. */}
      <Card className="px-6 py-5">
        <h2 className="text-[16px] font-semibold text-foreground">Students</h2>
        <p className="mt-1.5 text-[14px] text-muted-foreground">
          Click a name to see their disc and submitted work.
        </p>
        <ul className="mt-4 max-h-72 divide-y divide-border overflow-y-auto">
          {students.map((s) => {
            const assessed = s.skills_graded > 0;
            return (
              <li key={s.student_id}>
                <Link
                  to={demoStudentPath(cardId, s.student_id)}
                  className="flex items-center gap-3 py-2.5 transition-colors hover:text-primary"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-2xl bg-primary text-[12px] font-bold text-primary-foreground">
                    {initialsOf(s.student_name)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[14.5px] font-medium text-foreground">
                    {s.student_name}
                  </span>
                  <span
                    className={
                      assessed
                        ? 'shrink-0 text-[12.5px] font-medium text-primary'
                        : 'shrink-0 text-[12.5px] text-muted-foreground'
                    }
                  >
                    {assessed ? 'View progress →' : 'Not assessed'}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </Card>

      {/* What the project measures is known before any grading happens. */}
      {standardsCoverage.length > 0 && (
        <div className="max-h-72 overflow-y-auto rounded-xl">
          <StandardsCoverage
            rows={standardsCoverage}
            selectedStandardPath={filter?.kind === 'standard' ? filter.id : null}
            onSelectStandard={selectStandard}
          />
        </div>
      )}

      {graded > 0 && (
        <>
          <Card className="px-6 py-5">
            <h2 className="text-[16px] font-semibold text-foreground">Performance by competency</h2>
            <p className="mt-1.5 text-[14px] text-muted-foreground">
              Class average for each competency, averaged across its skill developmental descriptors.
              Click a bar to filter the matrix below.
            </p>
            <div className="-mx-2 mt-3">
              <CompetencyPerformance
                skills={skills}
                selectedCompetencyId={filter?.kind === 'competency_id' ? filter.id : null}
                onSelectCompetency={(id) => setFilter(id ? { kind: 'competency_id', id } : null)}
              />
            </div>
          </Card>

          <Card className="px-6 py-5">
            <h2 className="text-[16px] font-semibold text-foreground">
              Skill developmental descriptors needing attention
            </h2>
            <p className="mt-1.5 text-[14px] text-muted-foreground">
              All {skills.length}{' '}
              <strong className="font-semibold text-foreground">skill developmental descriptors</strong>{' '}
              ranked by lowest mastery — the % of graded students scoring at level 4+ — with the five
              most in need shown first. Click a row to filter the matrix below.
            </p>
            <div className="-mx-2 mt-3">
              <SkillsNeedingAttention
                skills={skills}
                selectedSkillId={filter?.kind === 'specific_skill_id' ? filter.id : null}
                onSelectSkill={(id) => setFilter(id ? { kind: 'specific_skill_id', id } : null)}
              />
            </div>
          </Card>

          <div ref={matrixRef} className="scroll-mt-6">
            <Card className="pt-5">
              <div className="px-6">
                <h2 className="text-[16px] font-semibold text-foreground">
                  Students × Skill Developmental Descriptors
                </h2>
                <p className="mt-1.5 text-[14px] text-muted-foreground">
                  Each row is a skill developmental descriptor; each column is a student. A cell is that
                  student’s score on that descriptor. Red horizontal stripe = re-teach it; red vertical
                  stripe = check on the student.{' '}
                  <strong className="font-semibold text-foreground">Click a cell</strong> for the
                  evidence and rationale behind the score;{' '}
                  <strong className="font-semibold text-foreground">click a student’s name</strong> to
                  open their submitted work.
                </p>
                {filter && (
                  <button
                    type="button"
                    onClick={() => setFilter(null)}
                    className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary bg-card px-3.5 py-1 text-[12.5px] font-medium text-primary"
                  >
                    Showing only{' '}
                    {filter.kind === 'standard'
                      ? filter.label
                      : filter.kind === 'competency_id'
                        ? (skills.find((s) => s.competency_id === filter.id)?.competency ?? 'one competency')
                        : (skills.find((s) => s.specific_skill_id === filter.id)?.specific_skill ??
                          'one skill developmental descriptor')}
                    <span aria-hidden className="font-bold">
                      ×
                    </span>
                  </button>
                )}
              </div>
              <div className="mt-4 overflow-hidden rounded-b-2xl">
                <SkillHeatmap
                  skills={skills}
                  students={students}
                  matrix={matrix}
                  specificSkillIdFilter={filteredSpecificSkillIds}
                  onCellClick={(student, skillRow) => setCellDetail({ student, skillRow })}
                  onStudentClick={setViewerStudent}
                />
              </div>
            </Card>
          </div>

          <GradeDetailModal
            open={cellDetail != null}
            onClose={() => setCellDetail(null)}
            studentName={cellDetail?.student.student_name}
            skillName={cellDetail?.skillRow.specific_skill}
            breadcrumb={cellDetail?.skillRow ?? null}
            grades={cellGrades}
            loading={gradesQuery.isLoading}
            discHref={cellDetail ? demoStudentPath(cardId, cellDetail.student.student_id) : undefined}
            onOpenSubmission={() => {
              setViewerStudent(cellDetail?.student ?? null);
              setCellDetail(null);
            }}
            onOpenWork={(_g, anchor) => {
              setViewerAnchor(anchor ?? null);
              setViewerStudent(cellDetail?.student ?? null);
            }}
          />
        </>
      )}

      <SubmissionViewer
        projectId={adProjectId}
        student={viewerStudent}
        anchor={viewerAnchor}
        onClose={() => {
          setViewerStudent(null);
          setViewerAnchor(null);
        }}
      />
    </div>
  );
}
